import { OrbitalTrackAttachStatus } from '@apeiron/orbital'
import { StarLite } from '@apeiron/star'
import { createStrictContext, MayNull, useAccountContext } from '@apeiron/temp'
import { useWalletAddress } from '@apeiron/wallet'
import useGetDetachDetail from '@src/hooks/graphql/useGetDetachDetail'
import { useGetStarByIDs } from '@src/hooks/graphql/useGetStarByIDs'
import {
  OrbitalTrackCompleteDetachDetail,
  OrbitalTrackDetachDetail,
  OrbitalTrackLite
} from '@src/types/orbitalTrack'
import * as R from 'ramda'
import * as RA from 'ramda-adjunct'
import {
  Dispatch,
  FC,
  ReactChild,
  SetStateAction,
  useCallback,
  useState
} from 'react'

const [ContextProvider, useOrbitalTrackRenewDetachContext] =
  createStrictContext<ContextType>('OrbitalTrackRenewDetach')

export { useOrbitalTrackRenewDetachContext }

export const OrbitalTrackRenewDetachProvider: FC<Props> = props => {
  const { children } = props

  const {
    fetch: fetchDetachDetail,
    data: detachDetails,
    loading: loadingDetach
  } = useGetDetachDetail()

  const {
    fetch: fetchStars,
    data: stars,
    loading: loadingStar
  } = useGetStarByIDs()

  const [checked, setChecked] = useState<boolean>(false)

  const [done, setDone] = useState<boolean>(false)

  const [failIds, setFailIds] = useState<string[]>([])

  const [successIds, setSuccessIds] = useState<string[]>([])

  const { account } = useAccountContext()

  const { compareAddress } = useWalletAddress()

  const checkAttachStatus = useCallback(
    (
      loading: boolean,
      star: MayNull<StarLite>,
      track: MayNull<OrbitalTrackLite>,
      detachDetail: MayNull<OrbitalTrackDetachDetail>
    ): OrbitalTrackAttachStatus => {
      if (
        loading ||
        R.isNil(star) ||
        R.isNil(track) ||
        RA.isNilOrEmpty(account.walletAddress)
      ) {
        return OrbitalTrackAttachStatus.Loading
      }

      const starAddress = R.pathOr('', ['owner', 'walletAddress'], star)

      const planetAddress = R.propOr('', 'planetOwnerAddress', track) as string

      const noRentRecord = R.isNil(detachDetail)

      const isStarOwner = compareAddress(starAddress, account.walletAddress)

      const isPlanetOwner = compareAddress(planetAddress, account.walletAddress)

      if (isStarOwner && isPlanetOwner) {
        return OrbitalTrackAttachStatus.SelfAttach
      }

      if (isPlanetOwner) {
        return noRentRecord
          ? OrbitalTrackAttachStatus.TransferredAttach // self attach > sell star
          : OrbitalTrackAttachStatus.RentAttach
      }

      return OrbitalTrackAttachStatus.NoAttach
    },
    [account.walletAddress]
  )

  const fetchRentalDetachDetail = useCallback(
    async (
      tracks: OrbitalTrackLite[]
    ): Promise<OrbitalTrackCompleteDetachDetail[]> => {
      const serialized = R.map((track: OrbitalTrackLite) => {
        const isStarOwner = compareAddress(
          track.star?.owner?.walletAddress,
          account.walletAddress
        )

        return {
          isDetachByStarOwner: isStarOwner && !track.isOwnerAttaching,
          originalRentalStartTime: track.firstRentalStartTime,
          planetID: track.planetId,
          slotIndex: track.slotIndex,
          starID: track.starId
        }
      }, tracks)

      const { data } = await fetchDetachDetail(serialized)

      const starIds = R.map(R.path(['inputParam', 'starID']), data) as string[]

      fetchStars(R.dropRepeats(starIds))

      return data
    },
    [account.walletAddress, compareAddress, fetchDetachDetail, fetchStars]
  )

  const resetOperation = useCallback(() => {
    setDone(false)
    setFailIds([])
    setSuccessIds([])
  }, [])

  return (
    <ContextProvider
      value={{
        checked,
        detachDetails,
        done,
        failIds,
        loading: loadingDetach || loadingStar,
        stars,
        successIds,
        checkAttachStatus,
        fetchRentalDetachDetail,
        resetOperation,
        setChecked,
        setDone,
        setFailIds,
        setSuccessIds
      }}
    >
      {children}
    </ContextProvider>
  )
}

type ContextType = {
  checked: boolean
  detachDetails: OrbitalTrackCompleteDetachDetail[]
  done: boolean
  failIds: string[]
  loading: boolean
  stars: StarLite[]
  successIds: string[]
  checkAttachStatus: (
    loading: boolean,
    star: MayNull<StarLite>,
    track: MayNull<OrbitalTrackLite>,
    detachDetail: MayNull<OrbitalTrackDetachDetail>
  ) => OrbitalTrackAttachStatus
  fetchRentalDetachDetail: (
    tracks: OrbitalTrackLite[]
  ) => Promise<OrbitalTrackCompleteDetachDetail[]>
  resetOperation: () => void
  setChecked: Dispatch<SetStateAction<boolean>>
  setDone: Dispatch<SetStateAction<boolean>>
  setFailIds: Dispatch<SetStateAction<string[]>>
  setSuccessIds: Dispatch<SetStateAction<string[]>>
}

export type Props = {
  children: ReactChild | ReactChild[]
}
